/**
 * VDOT running calculator — Jack Daniels' formula.
 *
 * Implements the canonical Daniels equation set, reproduced from the
 * `tlgs/vdot` reference implementation (which builds the official VDOT
 * lookup table via exactly this algorithm):
 *
 *   VO2 (ml/kg/min) = -4.60 + 0.182258·v + 0.000104·v²     (v in m/min)
 *   %VO2max        = 0.8 + 0.1894393·e^(-0.012778·t)
 *                      + 0.2989558·e^(-0.1932605·t)         (t in min)
 *   VDOT           = VO2 / %VO2max
 *
 * Equivalent race times are the analytical inverse (bisection on t).
 * Training paces invert the VO2 polynomial at a target %VO2max per zone.
 *
 * References:
 *  - Daniels, J. "Daniels' Running Formula" (3rd ed., Human Kinetics).
 *  - https://github.com/tlgs/vdot  (table generator, scipy bisection)
 *  - https://github.com/st3v/running-formulas-mcp  (verified VDOT vectors)
 *
 * Verified VDOT vectors (this implementation matches to 0.05):
 *   5000 m @ 25:00  -> 38.3   (st3v test_server.py)
 *   10000 m @ 60:00 -> 32.3
 *   1500 m @ 4:00   -> 70.1
 *   marathon @ 2:00 -> 86.0
 */

import type {
  EquivalentPerformance,
  RaceDistance,
  RaceResult,
  TrainingPaces,
  VdotResult,
} from '../shared/types';

/* ----------------------------- constants ------------------------------ */

/** Metres covered by each supported race distance. */
export const RACE_DISTANCE_METRES: Record<RaceDistance, number> = {
  '1500m': 1500,
  '1mile': 1609.344,
  '3K': 3000,
  '5K': 5000,
  '10K': 10000,
  half: 21097.5,
  marathon: 42195,
};

/** Human labels for each distance, in display order. */
export const RACE_DISTANCE_LABELS: Record<RaceDistance, string> = {
  '1500m': '1500 m',
  '1mile': 'Mile',
  '3K': '3K',
  '5K': '5K',
  '10K': '10K',
  half: 'Half marathon',
  marathon: 'Marathon',
};

/** Distances predicted as "equivalent performances" from a PB. */
const EQUIVALENT_DISTANCES: RaceDistance[] = [
  '1500m',
  '1mile',
  '3K',
  '5K',
  '10K',
  'half',
  'marathon',
];

/**
 * Fraction of VO2max targeted by each training zone — the Daniels
 * book-faithful values used by tlgs/vdot's table generator.
 */
const ZONE_FRACTION = {
  easySlow: 0.6304,
  easyFast: 0.7346,
  threshold: 0.8799,
  interval: 0.9743,
} as const;

/* --------------------------- core formula ----------------------------- */

/**
 * Compute VDOT (a proxy for VO2max) from a single race performance.
 *
 * @param distanceM  race distance in metres
 * @param timeS      finish time in seconds
 * @returns VDOT score (unitless, ~15 walk .. ~85 elite)
 */
export function calculateVdot(distanceM: number, timeS: number): number {
  const t = timeS / 60; // minutes
  const v = distanceM / t; // metres per minute
  const vo2 = -4.6 + 0.182258 * v + 0.000104 * v * v;
  const pctVo2max =
    0.8
    + 0.1894393 * Math.exp(-0.012778 * t)
    + 0.2989558 * Math.exp(-0.1932605 * t);
  return vo2 / pctVo2max;
}

/* ----------------------- equivalent race times ------------------------ */

/**
 * Objective function for inverting the Daniels equation: find time t
 * (minutes) that yields `targetVdot` for distance `d` (metres).
 *
 * Re-arranged from VO2/%VO2max with v = d/t:
 *   VO2(t)      = -4.6 + 0.182258·(d/t) + 0.000104·(d/t)²
 *   %VO2max(t)  = 0.8 + 0.1894393·e^(-0.012778t) + 0.2989558·e^(-0.1932605t)
 *   f(t)        = VO2(t)/%VO2max(t) − targetVdot
 */
function vdotTimeResidual(tMin: number, targetVdot: number, d: number): number {
  const invT = 1 / tMin;
  const vo2 = -4.6 + 0.182258 * d * invT + 0.000104 * d * d * invT * invT;
  const pct =
    0.8
    + 0.1894393 * Math.exp(-0.012778 * tMin)
    + 0.2989558 * Math.exp(-0.1932605 * tMin);
  return vo2 / pct - targetVdot;
}

/**
 * Invert the Daniels equation to find the finish time (seconds) for
 * distance `d` at a given VDOT, via bisection (monotonic in t).
 *
 * @param targetVdot  VDOT score to solve for
 * @param distanceM   target distance in metres
 * @returns finish time in seconds
 */
export function timeForVdot(targetVdot: number, distanceM: number): number {
  // Search 1 min .. 600 min (600 min = 10 h, covers the slowest marathon).
  let lo = 1;
  let hi = 600;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    // Note: f() is decreasing in t (faster time → higher VDOT), so f(lo)>0.
    const fMid = vdotTimeResidual(mid, targetVdot, distanceM);
    if (fMid > 0) {
      lo = mid;
    } else {
      hi = mid;
    }
    if (hi - lo < 1e-9) break;
  }
  return ((lo + hi) / 2) * 60; // seconds
}

/**
 * Predict equivalent finish times across all standard distances for a
 * given VDOT score.
 */
export function predictEquivalents(
  targetVdot: number,
  distances: RaceDistance[] = EQUIVALENT_DISTANCES,
): EquivalentPerformance[] {
  return distances.map((distance) => {
    const metres = RACE_DISTANCE_METRES[distance];
    return {
      distance,
      label: RACE_DISTANCE_LABELS[distance],
      timeSeconds: timeForVdot(targetVdot, metres),
    };
  });
}

/* --------------------------- training paces --------------------------- */

/**
 * Invert the VO2 polynomial to find running velocity (m/min) that elicits
 * a target fraction `p` of VO2max at a given VDOT.
 *
 * Solves  0.000104·v² + 0.182258·v − (4.6 + VDOT·p) = 0  for v > 0.
 * (tlgs `g()` uses: 0.033218 = 0.182258², 0.000416 = 4·0.000104,
 *  denom 0.000208 = 2·0.000104 — identical quadratic-formula form.)
 */
function velocityForFraction(vdot: number, fraction: number): number {
  const discriminant = 0.033218 - 0.000416 * (-4.6 - vdot * fraction);
  return (-0.182258 + Math.sqrt(discriminant)) / 0.000208;
}

/** Convert velocity (m/min) to pace in seconds per kilometre. */
function velocityToSecPerKm(velocityMperMin: number): number {
  return (1000 / velocityMperMin) * 60;
}

/**
 * Compute Daniels training paces (s/km) for a given VDOT.
 *
 * Easy (range), Threshold, Interval use the polynomial inversion at each
 * zone's %VO2max. Marathon pace is derived from the predicted marathon
 * time. Repetition pace is Interval pace minus 20 s/km for VDOT < 50
 * (slower runners) or 15 s/km otherwise — exactly as tlgs builds the table.
 */
export function calculateTrainingPaces(vdot: number): TrainingPaces {
  const easySlow = Math.round(velocityToSecPerKm(velocityForFraction(vdot, ZONE_FRACTION.easySlow)));
  const easyFast = Math.round(velocityToSecPerKm(velocityForFraction(vdot, ZONE_FRACTION.easyFast)));
  const threshold = Math.round(velocityToSecPerKm(velocityForFraction(vdot, ZONE_FRACTION.threshold)));
  const interval = Math.round(velocityToSecPerKm(velocityForFraction(vdot, ZONE_FRACTION.interval)));

  // Marathon pace from the predicted marathon time / 42.195 km.
  const marathonTimeS = timeForVdot(vdot, RACE_DISTANCE_METRES.marathon);
  const marathon = Math.round(marathonTimeS / 42.195);

  const repetition = interval - (vdot < 50.0 ? 20 : 15);

  return { easySlow, easyFast, marathon, threshold, interval, repetition };
}

/* --------------------------- full pipeline ---------------------------- */

/**
 * Full VDOT calculation: validate input, compute VDOT, predict equivalent
 * performances and training paces.
 */
export function calculateVdotResult(input: RaceResult): VdotResult {
  const distanceM = RACE_DISTANCE_METRES[input.distance];
  if (!distanceM || distanceM <= 0) {
    return { error: `Unknown distance: ${input.distance}` };
  }
  if (!Number.isFinite(input.timeSeconds) || input.timeSeconds <= 0) {
    return { error: 'Finish time must be a positive number of seconds.' };
  }
  // Sanity bounds: sub-1-minute races / multi-day finishes are not meaningful.
  if (input.timeSeconds < 30) {
    return { error: 'Finish time is unrealistically fast.' };
  }
  if (input.timeSeconds > 12 * 3600) {
    return { error: 'Finish time is unrealistically slow.' };
  }

  const vdot = calculateVdot(distanceM, input.timeSeconds);
  if (!Number.isFinite(vdot) || vdot <= 0) {
    return { error: 'Could not compute a valid VDOT for that performance.' };
  }

  const equivalents = predictEquivalents(vdot);
  const paces = calculateTrainingPaces(vdot);

  return { vdot, input, equivalents, paces };
}
